// Objectives store. Two backends: localStorage (mock/offline) and API-backed
// (Cognito + API Gateway). API mode merges shared video_content objectives with
// the per-user `user_objectives` overlay.

import {
  addObjective as apiAddObjective,
  deleteObjective as apiDeleteObjective,
  getVideoDetail,
  patchObjective as apiPatchObjective,
  toVideoObjectives as toVideoObjectivesFromApi,
} from "./api-client";
import { TokenProvider } from "./library-store";

async function requireToken(getToken: TokenProvider): Promise<string> {
  const t = await getToken();
  if (!t) throw new Error("Sign in again to continue.");
  return t;
}

export interface Objective {
  id: string;
  title: string;
  detail: string;
  timestampSec: number | null;
}

export type Instrument = "guitar" | "bass" | "ukulele" | "unknown";
export type SkillCategory = "Technique" | "Theory" | "Songs";

export interface VideoObjectives {
  youtubeVideoId: string;
  summary: string;
  instrument: Instrument;
  skillCategory: SkillCategory;
  transcriptSource?: "captions" | "whisper" | null;
  objectives: Objective[];
}

export interface ObjectivesStore {
  get(youtubeVideoId: string): Promise<VideoObjectives | null>;
  put(record: VideoObjectives): Promise<void>;
  updateObjective(
    youtubeVideoId: string,
    objectiveId: string,
    patch: Partial<Objective>
  ): Promise<VideoObjectives | null>;
  addObjective(youtubeVideoId: string, objective: Objective): Promise<VideoObjectives | null>;
  removeObjective(youtubeVideoId: string, objectiveId: string): Promise<VideoObjectives | null>;
}

const STORAGE_KEY = "fretboard.objectives";

export function createLocalObjectivesStore(): ObjectivesStore {
  function readAll(): Record<string, VideoObjectives> {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    try {
      return JSON.parse(raw) as Record<string, VideoObjectives>;
    } catch {
      return {};
    }
  }

  function writeAll(all: Record<string, VideoObjectives>): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  }

  return {
    async get(id) {
      return readAll()[id] ?? null;
    },
    async put(record) {
      const all = readAll();
      all[record.youtubeVideoId] = record;
      writeAll(all);
    },
    async updateObjective(videoId, objectiveId, patch) {
      const all = readAll();
      const rec = all[videoId];
      if (!rec) return null;
      const idx = rec.objectives.findIndex((o) => o.id === objectiveId);
      if (idx < 0) return null;
      rec.objectives[idx] = { ...rec.objectives[idx], ...patch };
      writeAll(all);
      return rec;
    },
    async addObjective(videoId, objective) {
      const all = readAll();
      const rec = all[videoId];
      if (!rec) return null;
      rec.objectives.push(objective);
      writeAll(all);
      return rec;
    },
    async removeObjective(videoId, objectiveId) {
      const all = readAll();
      const rec = all[videoId];
      if (!rec) return null;
      rec.objectives = rec.objectives.filter((o) => o.id !== objectiveId);
      writeAll(all);
      return rec;
    },
  };
}

export function newObjectiveId(): string {
  return `obj_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

// API-backed objectives store. Reads use the unified detail endpoint and
// reshape into VideoObjectives. Mutations call user-scoped overlay routes.
export function createApiObjectivesStore(getToken: TokenProvider): ObjectivesStore {
  async function fetchRecord(videoId: string): Promise<VideoObjectives | null> {
    const detail = await getVideoDetail(videoId, await requireToken(getToken));
    if (!detail) return null;
    return toVideoObjectivesFromApi(videoId, detail.content, detail.objectives);
  }

  return {
    async get(id) {
      return fetchRecord(id);
    },
    async put(_record) {
      // Server is authoritative for shared content; no-op for API mode.
      // (Local sample-objectives flow doesn't apply once API is configured.)
    },
    async updateObjective(videoId, objectiveId, patch) {
      const token = await requireToken(getToken);
      await apiPatchObjective(
        videoId,
        objectiveId,
        {
          ...(patch.title !== undefined ? { title: patch.title } : {}),
          ...(patch.detail !== undefined ? { detail: patch.detail } : {}),
          ...(patch.timestampSec !== undefined ? { timestampSec: patch.timestampSec } : {}),
        },
        token
      );
      return fetchRecord(videoId);
    },
    async addObjective(videoId, objective) {
      await apiAddObjective(
        videoId,
        {
          title: objective.title,
          detail: objective.detail,
          timestampSec: objective.timestampSec,
        },
        await requireToken(getToken)
      );
      return fetchRecord(videoId);
    },
    async removeObjective(videoId, objectiveId) {
      await apiDeleteObjective(videoId, objectiveId, await requireToken(getToken));
      return fetchRecord(videoId);
    },
  };
}

// Stub objectives — used until the spike's JSON output (or real Lambda) is wired in.
export function sampleObjectivesFor(youtubeVideoId: string): VideoObjectives {
  return {
    youtubeVideoId,
    summary: "Sample objectives — replace by running the M1.4 transcription pipeline.",
    instrument: "guitar",
    skillCategory: "Technique",
    objectives: [
      {
        id: newObjectiveId(),
        title: "Warm up with chromatic runs across all six strings",
        detail: "Focus on alternate picking and even tone.",
        timestampSec: 15,
      },
      {
        id: newObjectiveId(),
        title: "Play the F major barre chord cleanly",
        detail: "All six strings ring out without buzzing.",
        timestampSec: 180,
      },
      {
        id: newObjectiveId(),
        title: "Transition smoothly between C, G, and Am",
        detail: "Aim for 60 BPM, no pauses on the chord changes.",
        timestampSec: 320,
      },
      {
        id: newObjectiveId(),
        title: "Apply consistent strumming pattern (D-D-U-U-D-U)",
        detail: "Keep your wrist loose; metronome at 80 BPM.",
        timestampSec: 540,
      },
    ],
  };
}
